import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import Text from "@components/Text";
import Button from "@components/Button";
import Input from "@components/Input";
import Spinner from "@components/Spinner";
import Dialog from "@components/Dialog";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import {
  getApiKeys,
  createApiKey,
  revokeApiKey,
  AdminApiKey,
} from "@services/adminService";
import { useStores } from "@stores";
import { toast } from "react-toastify";

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Table = styled.div`
  width: 100%;
  border: 1px solid #ebeeef;
  border-radius: 6px;
  overflow: auto;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin: 0 -16px;
    border-radius: 0;
    border-left: none;
    border-right: none;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1.5fr 1.5fr 1fr 1fr 1fr 80px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #ebeeef;
  min-width: 800px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1.5fr 1.5fr 1fr 1fr 1fr 80px;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  align-items: center;
  min-width: 800px;
  &:last-of-type {
    border-bottom: none;
  }
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const ErrorWrap = styled.div`
  padding: 24px;
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 6px;
  margin-bottom: 16px;
`;

const Badge = styled.span`
  display: inline-block;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #494c5b;
  background: #f0f1f3;
  border-radius: 12px;
  padding: 4px 12px;
  margin-bottom: 16px;
`;

const RevokeButton = styled(Button)`
  width: auto;
  min-width: auto;
  padding: 4px 12px;
  font-size: 12px;
`;

// Modal form styles
const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const Label = styled.label`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #494c5b;
`;

const FormRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
`;

const KeyDisplayBox = styled.div`
  background: #2d2d2d;
  color: #f8f8f2;
  padding: 12px 16px;
  border-radius: 6px;
  font-family: monospace;
  font-size: 13px;
  word-break: break-all;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
`;

const CopyButton = styled(Button)`
  flex-shrink: 0;
  width: auto;
  min-width: 80px;
`;

const WarningBox = styled.div`
  background: #fff8e1;
  border-left: 4px solid #f0ad4e;
  padding: 12px 16px;
  margin-top: 16px;
  font-size: 13px;
  color: #6d5a00;
  border-radius: 0 4px 4px 0;
`;

const HelpText = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  color: #899093;
  line-height: 1.4;
`;

// Pill selector, used by the expiry presets. (Named ScopesGrid/ScopeChip while
// the scopes picker still existed — it was the other, larger user.)
const ChipGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`;

const Chip = styled.label<{ checked?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 16px;
  border: 1px solid ${({ checked }) => (checked ? "#4a90d9" : "#ebeeef")};
  background: ${({ checked }) => (checked ? "#eef4fc" : "#fff")};
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #494c5b;
  user-select: none;
  transition: all 0.15s;
  &:hover {
    border-color: #4a90d9;
  }
`;

/**
 * Expiry presets. `days: null` means the key never expires — a deliberate,
 * supported option for keys handed to customer integrations, which should not
 * silently stop working a year later.
 */
const EXPIRY_PRESETS: { label: string; days: number | null }[] = [
  { label: "Never", days: null },
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "1 year", days: 365 },
];
const CUSTOM_EXPIRY = "Custom…";
const DEFAULT_EXPIRY_LABEL = "1 year";



function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const CreateKeyModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ visible, onClose, onSuccess }) => {
  const { accountStore } = useStores();
  const [name, setName] = useState("");
  const [userId, setUserId] = useState("");
  const [expiryChoice, setExpiryChoice] = useState<string>(DEFAULT_EXPIRY_LABEL);
  const [expiresInDays, setExpiresInDays] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setName("");
      setUserId("");
      setExpiryChoice(DEFAULT_EXPIRY_LABEL);
      setExpiresInDays("");
      setCreatedKey(null);
    }
  }, [visible]);

  // null = never expires (the backend treats null as "no expiry").
  const resolveExpiresInDays = (): number | null => {
    if (expiryChoice === CUSTOM_EXPIRY) {
      const n = Number(expiresInDays);
      return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
    }
    return EXPIRY_PRESETS.find((p) => p.label === expiryChoice)?.days ?? null;
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    if (expiryChoice === CUSTOM_EXPIRY && !(Number(expiresInDays) > 0)) {
      toast.error("Enter a number of days, or pick a preset");
      return;
    }
    setSubmitting(true);
    try {
      const res = await createApiKey({
        name: name.trim(),
        userId: userId.trim() || undefined,
        expiresInDays: resolveExpiresInDays(),
      });
      setCreatedKey(res.key);
    } catch (e: any) {
      toast.error(e.message || "Failed to create API key");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = () => {
    if (createdKey) {
      navigator.clipboard.writeText(createdKey);
      toast.success("API key copied to clipboard");
    }
  };

  const handleClose = () => {
    if (createdKey) onSuccess();
    onClose();
  };

  return (
    <Dialog
      destroyOnClose
      visible={visible}
      onClose={handleClose}
      title={createdKey ? "API Key Created" : "Create New API Key"}
      style={{ width: 500 }}
    >
      {createdKey ? (
        <>
          <WarningBox>
            <strong>⚠️ Important:</strong> This is the only time the full API key
            will be displayed. Store it securely. You will not be able to see it
            again.
          </WarningBox>
          <KeyDisplayBox>
            <span style={{ flex: 1 }}>{createdKey}</span>
            <CopyButton kind="outline" onClick={handleCopy}>
              Copy
            </CopyButton>
          </KeyDisplayBox>
          <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={handleClose}>Done</Button>
          </div>
        </>
      ) : (
        <>
          <FormField>
            <Label>Name *</Label>
            <Input
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value)
              }
              placeholder="e.g. Script for yield upload"
            />
          </FormField>
          <FormField>
            <Label>User ID (optional)</Label>
            <HelpText>
              The API key is linked to a user for identity and audit purposes.
              Authorization (admin access, data across all users) is based on whether the user's
              email is in ADMIN_EMAILS. For scripts that read/write data for all users,
              leave this empty — your admin account will grant full access.
              Leave empty to use your own account ({accountStore.account?.email || "current user"}).
            </HelpText>
            <Input
              value={userId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setUserId(e.target.value)
              }
              placeholder="Leave empty for current user"
            />
          </FormField>
          <FormField>
            <Label>Expiry</Label>
            <HelpText>
              Pick a lifetime, or choose Never for a key handed to a customer
              integration. An expired key stops working immediately; either way
              you can revoke a key at any time.
            </HelpText>
            <ChipGrid>
              {EXPIRY_PRESETS.map((preset) => (
                <Chip
                  key={preset.label}
                  checked={expiryChoice === preset.label}
                  onClick={() => setExpiryChoice(preset.label)}
                >
                  {preset.label}
                </Chip>
              ))}
              <Chip
                checked={expiryChoice === CUSTOM_EXPIRY}
                onClick={() => setExpiryChoice(CUSTOM_EXPIRY)}
              >
                {CUSTOM_EXPIRY}
              </Chip>
            </ChipGrid>
            {expiryChoice === CUSTOM_EXPIRY && (
              <Input
                type="number"
                min={1}
                max={3650}
                value={expiresInDays}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setExpiresInDays(e.target.value)
                }
                placeholder="Number of days, e.g. 90"
              />
            )}
          </FormField>
          {/*
            There was a Scopes picker here, offering ten checkboxes under the
            label "Choose which actions this API key can perform". Nothing ever
            read them: the backend stores api_keys.scopes and echoes it back,
            but requireAuth grants FULL_ACCESS to every valid key regardless
            (backend-ai src/middleware/auth.ts), and admin routes authorise on
            the ADMIN_EMAILS allowlist, not on the key. So a key created with
            only "fields:read" ticked was a full credential — the picker's one
            practical effect was to make someone believe otherwise while handing
            it out. Removed rather than enforced: enforcement would have to
            revoke access that live integrations already rely on, and it would
            still not constrain an admin-owned key.

            Do not restore this UI unless the backend actually authorises on
            scopes. Until then this line is the truth about what a key grants.
          */}
          <FormField>
            <Label>Access</Label>
            <HelpText>
              An API key acts as its owner, with the owner's full access. Scope it
              by choosing whose account it belongs to, and limit exposure with the
              expiry above — revoke it here if it leaks.
            </HelpText>
          </FormField>
          <FormRow style={{ marginTop: 24 }}>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !name.trim()}
            >
              {submitting ? "Creating..." : "Create"}
            </Button>
            <Button kind="outline" onClick={handleClose}>
              Cancel
            </Button>
          </FormRow>
        </>
      )}
    </Dialog>
  );
};

const RevokeConfirmModal: React.FC<{
  visible: boolean;
  keyName: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}> = ({ visible, onClose, onConfirm, keyName }) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      title="Revoke API Key"
      style={{ width: 400 }}
    >
      <Text size={14} color="#494c5b">
        Are you sure you want to revoke <strong>{keyName}</strong>? This action
        cannot be undone.
      </Text>
      <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
        <Button onClick={handleConfirm} disabled={loading}>
          {loading ? "Revoking..." : "Revoke"}
        </Button>
        <Button kind="outline" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Dialog>
  );
};

const AdminApiKeysTab: React.FC = () => {
  const [keys, setKeys] = useState<AdminApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<AdminApiKey | null>(null);

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const data = await getApiKeys();
      setKeys(data.keys);
    } catch (e: any) {
      const msg = String(e?.message || "").toLowerCase();
      if (msg.includes("403") || msg.includes("forbidden") || msg.includes("super admin")) {
        setAccessDenied(true);
      } else {
        toast.error(e.message || "Failed to fetch API keys");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    await revokeApiKey(revokeTarget.id);
    toast.success("API key revoked");
    setRevokeTarget(null);
    fetchKeys();
  };

  return (
    <Container>
      {accessDenied && (
        <ErrorWrap>
          <Text size={14} color="#e74c3c">
            Access denied. Super Admin permissions required to manage API keys.
          </Text>
        </ErrorWrap>
      )}

      {!accessDenied && (
        <>
          <HeaderRow>
            <Badge>{keys.length} API key{keys.length !== 1 ? "s" : ""}</Badge>
            <Button onClick={() => setCreateModalOpen(true)}>
              Create New Key
            </Button>
          </HeaderRow>

          <Table>
            <TableHeader>
              <Text size={12} color="#899093" type="heading">
                Name
              </Text>
              <Text size={12} color="#899093" type="heading">
                Prefix
              </Text>
              <Text size={12} color="#899093" type="heading">
                Owner
              </Text>
              <Text size={12} color="#899093" type="heading">
                Last Used
              </Text>
              <Text size={12} color="#899093" type="heading">
                Expires At
              </Text>
              <Text size={12} color="#899093" type="heading">
                Created At
              </Text>
              <span />
            </TableHeader>

            {loading ? (
              <LoadingWrap>
                <Spinner />
              </LoadingWrap>
            ) : keys.length === 0 ? (
              <div style={{ padding: 48, textAlign: "center" }}>
                <Text size={14} color="#899093">
                  No API keys yet. Create one to get started.
                </Text>
              </div>
            ) : (
              keys.map((key) => (
                <TableRow key={key.id}>
                  <Text size={13} color="#494c5b">
                    {key.name}
                  </Text>
                  <Text size={13} color="#899093" style={{ fontFamily: "monospace" }}>
                    {key.prefix}
                  </Text>
                  <Text size={13} color="#494c5b">
                    {key.ownerName || key.ownerEmail || key.ownerId || "—"}
                  </Text>
                  <Text size={13} color="#494c5b">
                    {formatDate(key.lastUsedAt)}
                  </Text>
                  <Text size={13} color="#494c5b">
                    {formatDate(key.expiresAt)}
                  </Text>
                  <Text size={13} color="#494c5b">
                    {formatDate(key.createdAt)}
                  </Text>
                  <RevokeButton
                    kind="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRevokeTarget(key);
                    }}
                  >
                    Revoke
                  </RevokeButton>
                </TableRow>
              ))
            )}
          </Table>
        </>
      )}

      <CreateKeyModal
        visible={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={fetchKeys}
      />

      <RevokeConfirmModal
        visible={!!revokeTarget}
        keyName={revokeTarget?.name ?? ""}
        onClose={() => setRevokeTarget(null)}
        onConfirm={handleRevoke}
      />
    </Container>
  );
};

export default AdminApiKeysTab;
